import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { canUseGateway } from "../announcementPresentation";
import type { useAnnouncementConfig } from "./useAnnouncementConfig";
import { Button } from "./ui/Button";
import { Feedback } from "./ui/Surface";

/**
 * 발송 설정과 잔여 건수는 메세지를 쓰는 동안 알아야 하는 사실이다. ⓘ 버튼 뒤 팝업에 넣어
 * 두면 눌러 본 사람만 알게 되고, 팝업 안에서 또 "잔여 건수 조회"를 눌러야 숫자가 나왔다.
 * 작성 칸 아래 한 줄로 늘 펼쳐 두고, 잔여 건수는 열릴 때 스스로 불러온다.
 */
export function AnnouncementGatewayStatus({ config, sending }: { config: ReturnType<typeof useAnnouncementConfig>; sending: boolean }) {
  const [remaining, setRemaining] = useState<{ error: boolean; text: string } | null>(null), guard = useRef(false);
  const usable = canUseGateway(config.value);
  useEffect(() => {
    if (!usable || guard.current) return;
    guard.current = true;
    void (async () => {
      try {
        const value = await api.getSmsRemaining();
        setRemaining({ error: false, text: `잔여 SMS ${String(value.SMS_CNT ?? "-")}건 · LMS ${String(value.LMS_CNT ?? "-")}건 · MMS ${String(value.MMS_CNT ?? "-")}건` });
      } catch (error) {
        setRemaining({ error: true, text: error instanceof Error ? error.message : "잔여 건수를 조회하지 못했습니다." });
      }
    })();
  }, [usable]);

  if (config.loading) return <p className="message-gateway-status">전송 설정 확인 중…</p>;
  if (config.error) return <Feedback tone="error">{config.error}<Button size="compact" disabled={sending} onClick={() => void config.reload()}>설정 다시 확인</Button></Feedback>;
  if (!config.value) return null;
  /* 여기 적을 것은 "지금 보내면 어떻게 되는가"뿐이다. 테스트 번호와 발송 간격은 시험할 때만
     뜻이 있고 실제 발송에서는 아무도 볼 이유가 없어, 그때까지 줄에 남아 있으면 정작 중요한
     잔여 건수를 밀어낸다. 설정이 비어 있다는 말은 보낼 수 없다는 뜻이라 늘 남긴다. */
  const ready = config.value.accountConfigured && config.value.senderConfigured;
  const facts = [
    config.value.testMode ? "테스트 모드 · 실제 전송 없음" : "실제 발송",
    ...(ready ? [] : ["계정·발신번호 확인 필요"]),
    ...(config.value.testMode ? [`테스트 번호 ${config.value.testReceiverConfigured ? "설정됨" : "미설정"}`] : []),
  ];
  return <p className={`message-gateway-status${remaining?.error ? " error" : ""}`}>
    {facts.join(" · ")}
    {remaining && <> · {remaining.text}</>}
  </p>;
}
